// Abwesenheiten im Widget-Stil (design_handoff_personal §4, Screens 09–12, 20, 25): Urlaubskonto mit Ring,
// Antrags-Karten mit Verlauf, Teamkalender und Antrag-Dialog. Fachlich entscheidet weiterhin lib/zeiterfassung.
import { useState } from 'react';
import {
  CalendarBlank, CalendarCheck, CalendarPlus, ChatCircleDots, Check, CircleHalf, FileArrowDown, FileArrowUp, HourglassMedium,
  PaperPlaneTilt, WarningCircle, X, AirplaneTilt,
} from '@phosphor-icons/react';
import { DialogHuelle, FeldRaster, ImmoButton, TextFeld } from '../../../../components/immobilien/ui';
import { IosSchalter } from '../../../../components/ui/IosSchalter';
import { KennzahlWidget } from '../../../../components/ui/KennzahlWidget';
import { antragStellen, nachweisHochladen, type VerlaufEintrag } from '../../../../lib/zeiterfassung/api';
import {
  STATUS_INFO, TYP_INFO, antragFehler, tageDesAntrags, urlaubstageImAntrag, zeitraumText,
  type Abwesenheit, type AbwesenheitTyp,
} from '../../../../lib/zeiterfassung/abwesenheit';
import { tagVon } from '../../../../lib/zeiterfassung/berechnung';
import { feiertagName } from '../../../../lib/zeiterfassung/feiertage';
import { farbeAusName, urlaubsRingAnteil, verlaufAktion } from '../../../../lib/personalAnsicht/darstellung';
import { TYP_ICON } from './symbole';

const de = (n: number) => n.toLocaleString('de-DE');

// ─── Urlaubskonto (09) ──────────────────────────────────────────────────────────

/** Großes Akzent-Widget „Rest“ mit Ring (Rest/Anspruch), daneben Anspruch, Genommen, Beantragt — urlaubskonto aktuelles Jahr. */
export function UrlaubsKonto({ konto, mobil }: { konto: { anspruch: number; genommen: number; beantragt: number; rest: number }; mobil: boolean }) {
  const umfang = 2 * Math.PI * 50;
  const anteil = urlaubsRingAnteil(konto.rest, konto.anspruch);
  return (
    // Handy: Rest über die volle Breite, darunter Anspruch | Genommen, dann Beantragt (Handoff Screen 25).
    <div className="grid" style={{ gap: 12, gridTemplateColumns: mobil ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))' }}>
      <div data-tip="Resturlaub – Anspruch minus genommene und beantragte Tage" className="flex items-center"
        style={{ gridColumn: mobil ? '1 / -1' : undefined, borderRadius: 26, padding: '16px 18px', gap: 16, background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)' }}>
        <div className="relative shrink-0" style={{ width: 76, height: 76 }}>
          <svg viewBox="0 0 120 120" width={76} height={76} style={{ transform: 'rotate(-90deg)' }} aria-hidden>
            <circle cx={60} cy={60} r={50} fill="none" stroke="currentColor" strokeOpacity={0.22} strokeWidth={14} />
            {anteil > 0 && (
              <circle cx={60} cy={60} r={50} fill="none" stroke="currentColor" strokeWidth={14} strokeLinecap="round"
                strokeDasharray={`${(umfang * anteil).toFixed(1)} ${umfang.toFixed(1)}`} />
            )}
          </svg>
          <span className="absolute inset-0 flex items-center justify-center" style={{ fontSize: 20, fontWeight: 800 }}>{de(konto.rest)}</span>
        </div>
        <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, opacity: 0.85 }}>Rest</span>
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: konto.rest < 0 ? 'var(--bad)' : undefined }}>{de(konto.rest)} Tage</span>
          <span style={{ fontSize: 12, opacity: 0.8 }}>von {de(konto.anspruch)} Tagen</span>
        </div>
      </div>
      <KennzahlWidget label="Urlaubsanspruch" wert={`${de(konto.anspruch)} Tage`} unter="im laufenden Jahr" icon={CalendarCheck} farbe="var(--acc)"
        tip="Urlaubsanspruch – Tage im laufenden Jahr" />
      <KennzahlWidget label="Genommen" wert={de(konto.genommen)} unter="genehmigte Urlaubstage" icon={AirplaneTilt} farbe="var(--ok)"
        tip="Genommen – Genehmigte Urlaubstage" />
      <div className="grid" style={{ gridColumn: mobil ? '1 / -1' : undefined }}>
        <KennzahlWidget label="Beantragt" wert={de(konto.beantragt)} unter="noch nicht entschieden" icon={HourglassMedium} farbe="var(--warn)" wertFarbe="var(--warn)"
          tip="Beantragt – Offene Urlaubsanträge" />
      </div>
    </div>
  );
}

// ─── Antrags-Karte (10) ─────────────────────────────────────────────────────────

const RUND = { width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', flexShrink: 0 } as const;
const PILLE = { height: 36, padding: '0 14px', borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800, gap: 6 } as const;

export function AntragKarte({
  a, mitName, name, stufen, sollAmTag, darfEntscheiden, meineId, arbeitet,
  verlaufOffen, verlauf, kommentar, onKommentar, onVerlauf, onKommentieren, onEntscheiden, onStornieren, onNachweis,
}: {
  a: Abwesenheit; mitName: boolean; name: (id: string) => string; stufen: number; sollAmTag: (tag: string) => number;
  darfEntscheiden: boolean; meineId: string; arbeitet: boolean;
  verlaufOffen: boolean; verlauf: VerlaufEintrag[]; kommentar: string; onKommentar: (t: string) => void;
  onVerlauf: () => void; onKommentieren: () => void; onEntscheiden: (status: 'genehmigt' | 'abgelehnt') => void;
  onStornieren: () => void; onNachweis: () => void;
}) {
  const info = TYP_INFO[a.typ];
  const typFarbe = farbeAusName(info.farbe);
  const status = STATUS_INFO[a.status];
  const statusFarbe = farbeAusName(status.farbe);
  const tage = urlaubstageImAntrag(a, sollAmTag);
  const I = TYP_ICON[a.typ];
  return (
    <div className="immo-glas flex flex-col" style={{ borderRadius: 24, padding: '14px 16px', gap: 10 }}>
      <div className="flex flex-wrap items-center" style={{ gap: 14 }}>
        <span className="flex shrink-0 items-center justify-center" style={{ width: 44, height: 44, borderRadius: 14, background: typFarbe.flaeche }}>
          <I size={21} weight="fill" color={typFarbe.farbe} />
        </span>
        <div className="flex min-w-0 flex-col" style={{ flex: '1 1 240px', gap: 3 }}>
          <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
            {mitName && <span style={{ fontSize: 15, fontWeight: 800 }}>{name(a.mitarbeiter_id)}</span>}
            <span style={{ fontSize: 15, fontWeight: mitName ? 700 : 800, color: mitName ? 'var(--ink2)' : 'var(--ink)' }}>{info.label}</span>
          </div>
          <span style={{ fontSize: 13, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>
            {zeitraumText(a)} <b style={{ fontWeight: 700, color: 'var(--ink3)' }}>({de(tage)} {tage === 1 ? 'Tag' : 'Tage'})</b>
            {a.grund && <> · {a.grund}</>}
            {a.entscheidung_kommentar && <i> · {a.entscheidung_kommentar}</i>}
          </span>
        </div>
        {a.nachweis_path && (
          <button type="button" aria-label="Nachweis öffnen" data-tip="Nachweis öffnen – Arbeitsunfähigkeitsbescheinigung anzeigen" onClick={onNachweis}
            className="immo-tippziel flex items-center justify-center" style={{ ...RUND, background: 'var(--fill2)', color: 'var(--ink)' }}>
            <FileArrowDown size={15} weight="bold" />
          </button>
        )}
        {a.status === 'offen' && stufen > 1 && (
          <span data-tip="Freigabestufe – Wie weit der Antrag schon freigegeben ist" className="inline-flex items-center"
            style={{ height: 26, padding: '0 10px', borderRadius: 13, fontSize: 11.5, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>
            Stufe {a.freigabe_stufe ?? 1}/{stufen}
          </span>
        )}
        <span className="inline-flex items-center" style={{ height: 26, padding: '0 10px', borderRadius: 13, fontSize: 11.5, fontWeight: 800, background: statusFarbe.flaeche, color: statusFarbe.farbe }}>
          {status.label}
        </span>
        <button type="button" aria-label="Verlauf und Kommentare" aria-expanded={verlaufOffen} data-tip="Verlauf und Kommentare – Wer hat wann was entschieden" onClick={onVerlauf}
          className="immo-tippziel flex items-center justify-center" style={{ ...RUND, background: verlaufOffen ? 'var(--acc-soft)' : 'var(--fill2)', color: verlaufOffen ? 'var(--acc)' : 'var(--ink)' }}>
          <ChatCircleDots size={15} weight="bold" />
        </button>
        {a.status === 'offen' && darfEntscheiden && (
          <span className="flex" style={{ gap: 6 }}>
            <button type="button" data-tip="Genehmigen – Antrag freigeben" onClick={() => onEntscheiden('genehmigt')} disabled={arbeitet}
              className="immo-tippziel inline-flex items-center" style={{ ...PILLE, background: 'var(--ok)', color: '#fff', opacity: arbeitet ? 0.5 : 1 }}>
              <Check size={13} weight="bold" />Genehmigen
            </button>
            <button type="button" data-tip="Ablehnen – Antrag nicht freigeben" onClick={() => onEntscheiden('abgelehnt')} disabled={arbeitet}
              className="immo-tippziel inline-flex items-center" style={{ ...PILLE, background: 'var(--bad-soft)', color: 'var(--bad)', opacity: arbeitet ? 0.5 : 1 }}>
              <X size={13} weight="bold" />Ablehnen
            </button>
          </span>
        )}
        {a.mitarbeiter_id === meineId && (a.status === 'offen' || a.status === 'genehmigt') && (
          <button type="button" data-tip="Zurückziehen – Eigenen Antrag zurücknehmen" onClick={onStornieren} disabled={arbeitet}
            className="immo-tippziel inline-flex items-center" style={{ ...PILLE, background: 'var(--fill2)', color: 'var(--ink)' }}>
            Zurückziehen
          </button>
        )}
      </div>

      {verlaufOffen && (
        <div className="immo-pop-in flex flex-col" style={{ marginLeft: 'min(58px, 8%)', borderRadius: 18, padding: '12px 14px', gap: 8, background: 'var(--fill)' }}>
          {verlauf.length === 0 ? (
            <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>Noch kein Verlauf.</span>
          ) : verlauf.map((v) => {
            const akt = verlaufAktion(v.aktion, v.stufe);
            return (
              <div key={v.id} className="flex flex-wrap items-baseline" style={{ gap: 10, fontSize: 12.5 }}>
                <span className="shrink-0" style={{ width: 7, height: 7, borderRadius: '50%', background: akt.farbe, transform: 'translateY(-1px)' }} />
                <span style={{ color: 'var(--ink3)', fontVariantNumeric: 'tabular-nums' }}>
                  {new Date(v.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
                <b style={{ fontWeight: 800, color: akt.farbe }}>{akt.label}</b>
                <span style={{ fontWeight: 700 }}>{name(v.autor_id || '')}</span>
                <span style={{ color: 'var(--ink2)' }}>{v.kommentar}</span>
              </div>
            );
          })}
          <div className="flex" style={{ gap: 6 }}>
            <input value={kommentar} onChange={(e) => onKommentar(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') onKommentieren(); }}
              placeholder="Kommentar zum Antrag …" aria-label="Kommentar"
              className="min-w-0 flex-1" style={{ height: 38, padding: '0 12px', borderRadius: 12, border: 'none', outline: 'none', background: 'var(--bg)',
                boxShadow: 'inset 0 0 0 1px var(--line)', fontFamily: 'inherit', fontSize: 13, color: 'var(--ink)' }} />
            <button className="immo-tippziel" type="button" onClick={onKommentieren} data-tip="Senden – Kommentar zum Antrag speichern"
              style={{ height: 38, padding: '0 14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800,
                background: 'var(--acc-fill)', color: 'var(--acc-ink)' }}>Senden</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function LeerKarte({ text }: { text: string }) {
  return (
    <div className="immo-glas flex flex-col items-center" style={{ borderRadius: 26, padding: 36, gap: 8, color: 'var(--ink3)' }}>
      <CalendarBlank size={28} />
      <span style={{ fontSize: 14, fontWeight: 600 }}>{text}</span>
    </div>
  );
}

// ─── Teamkalender (11) ──────────────────────────────────────────────────────────

/** 28-Tage-Raster ab heute: WE/Feiertag grau, Typfarbe, offen = 40 % Deckkraft — Logik wie bisher. */
export function TeamKalender({ mitarbeiter, alle, tage, heute }: {
  mitarbeiter: { id: string; full_name: string | null; email: string | null }[]; alle: Abwesenheit[]; tage: string[]; heute: string;
}) {
  return (
    <>
      <div className="immo-glas immo-scroll overflow-x-auto" style={{ borderRadius: 26, padding: 14 }}>
        <div className="grid items-center" style={{ gridTemplateColumns: `150px repeat(${tage.length}, minmax(22px, 1fr))`, gap: 3, minWidth: 860 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Mitarbeiter</span>
          {tage.map((t) => {
            const d = new Date(`${t}T12:00:00`);
            const frei = [0, 6].includes(d.getDay()) || !!feiertagName(t);
            return (
              <div key={t} className="flex flex-col items-center" style={{ gap: 1, paddingBottom: 4, color: frei ? 'var(--ink3)' : t === heute ? 'var(--acc)' : 'var(--ink2)' }}>
                <span style={{ fontSize: 10, fontWeight: 700 }}>{d.toLocaleDateString('de-DE', { weekday: 'narrow' })}</span>
                <span style={{ fontSize: 12, fontWeight: 800 }}>{d.getDate()}</span>
              </div>
            );
          })}
          {mitarbeiter.map((m) => (
            <TeamZeile key={m.id} name={m.full_name || m.email || ''} id={m.id} alle={alle} tage={tage} heute={heute} />
          ))}
        </div>
      </div>
      <div className="flex flex-wrap" style={{ gap: 12, padding: '0 4px' }}>
        {Object.entries(TYP_INFO).map(([k, v]) => (
          <span key={k} className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>
            <span style={{ width: 12, height: 12, borderRadius: 4, background: farbeAusName(v.farbe).farbe }} />{v.label}
          </span>
        ))}
        <span className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>
          <span style={{ width: 12, height: 12, borderRadius: 4, background: 'var(--ink3)', opacity: 0.4 }} />blass = noch offen
        </span>
      </div>
    </>
  );
}

function TeamZeile({ name, id, alle, tage, heute }: { name: string; id: string; alle: Abwesenheit[]; tage: string[]; heute: string }) {
  return (
    <>
      <span className="truncate" style={{ fontSize: 13, fontWeight: 700, paddingRight: 8 }} title={name}>{name}</span>
      {tage.map((t) => {
        const a = alle.find((x) => x.mitarbeiter_id === id && x.status !== 'abgelehnt' && x.status !== 'storniert' && x.von_tag <= t && x.bis_tag >= t);
        const d = new Date(`${t}T12:00:00`);
        const ft = feiertagName(t);
        const frei = [0, 6].includes(d.getDay()) || !!ft;
        return (
          <div key={t} data-tip={a ? `${TYP_INFO[a.typ].label} (${STATUS_INFO[a.status].label})` : ft || undefined}
            style={{ height: 28, borderRadius: 8,
              background: a ? farbeAusName(TYP_INFO[a.typ].farbe).farbe : frei ? 'var(--fill3)' : 'var(--fill)',
              opacity: a && a.status === 'offen' ? 0.4 : 1,
              boxShadow: !a && t === heute ? 'inset 0 0 0 2px var(--acc)' : 'none' }} />
        );
      })}
    </>
  );
}

// ─── Antrag-Dialog (12, ersetzt das AntragFormular in der Pille) ────────────────

export function AntragDialog({ mitarbeiterId, sollAmTag, onFertig, onAbbrechen }: {
  mitarbeiterId: string; sollAmTag: (tag: string) => number; onFertig: () => void; onAbbrechen: () => void;
}) {
  const heute = tagVon(new Date().toISOString());
  const [typ, setTyp] = useState<AbwesenheitTyp>('urlaub');
  const [vonTag, setVonTag] = useState(heute);
  const [bisTag, setBisTag] = useState(heute);
  const [halberTag, setHalberTag] = useState(false);
  const [grund, setGrund] = useState('');
  const [nachweis, setNachweis] = useState<{ path: string; name: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const tage = urlaubstageImAntrag({ von_tag: vonTag, bis_tag: bisTag, halber_tag: halberTag }, sollAmTag);
  const feiertageImZeitraum = tageDesAntrags({ von_tag: vonTag, bis_tag: bisTag })
    .map((t) => feiertagName(t)).filter(Boolean) as string[];

  const senden = async () => {
    const problem = antragFehler({ von_tag: vonTag, bis_tag: bisTag, typ, halber_tag: halberTag });
    if (problem) { setFehler(problem); return; }
    setFehler(null);
    setSpeichert(true);
    const ok = await antragStellen({ mitarbeiterId, typ, vonTag, bisTag, halberTag, grund, nachweis });
    setSpeichert(false);
    if (!ok) { setFehler('Antrag konnte nicht gespeichert werden.'); return; }
    onFertig();
  };

  return (
    <DialogHuelle titel="Abwesenheit beantragen" icon={CalendarPlus} onSchliessen={onAbbrechen} laeuft={speichert} breite={520}
      fuss={<div className="flex w-full justify-end" style={{ gap: 10 }}>
        <ImmoButton groesse={44} onClick={onAbbrechen} disabled={speichert}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" groesse={44} icon={PaperPlaneTilt} onClick={() => void senden()} disabled={speichert}
          data-tip="Antrag stellen – zur Freigabe an die Personalabteilung">Antrag stellen</ImmoButton>
      </div>}>
      {fehler && (
        <div role="alert" className="flex items-start" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" style={{ marginTop: 1 }} />{fehler}
        </div>
      )}
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Art</span>
        <div role="radiogroup" aria-label="Art" className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))' }}>
          {(Object.keys(TYP_INFO) as AbwesenheitTyp[]).map((k) => {
            const an = k === typ;
            const f = farbeAusName(TYP_INFO[k].farbe);
            const I = TYP_ICON[k];
            return (
              <button data-tip={`${TYP_INFO[k].label} – ${TYP_INFO[k].urlaubstage ? 'zählt gegen den Jahresurlaub' : 'zählt nicht gegen den Urlaub'}`} key={k} type="button" role="radio" aria-checked={an} onClick={() => setTyp(k)}
                className="immo-hover-kachel flex flex-col items-center"
                style={{ borderRadius: 16, padding: '12px 4px 10px', gap: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: an ? f.flaeche : 'var(--fill2)', boxShadow: an ? `inset 0 0 0 2px ${f.farbe}` : 'none', transition: 'all .2s' }}>
                <I size={22} weight="fill" color={f.farbe} />
                <span style={{ fontSize: 11.5, fontWeight: an ? 800 : 700, color: 'var(--ink)', textAlign: 'center', lineHeight: 1.2 }}>{TYP_INFO[k].label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <FeldRaster minBreite={150}>
        <TextFeld label="Von" type="date" wert={vonTag} kompakt onChange={(w) => { setVonTag(w); if (w > bisTag) setBisTag(w); }} />
        <TextFeld label="Bis" type="date" wert={bisTag} min={vonTag} kompakt onChange={setBisTag} />
      </FeldRaster>

      <div className="flex items-center" style={{ gap: 10, padding: '10px 14px', borderRadius: 15, background: 'var(--fill)' }}>
        <CircleHalf size={17} weight="fill" color="var(--acc)" />
        <span className="flex-1" style={{ fontSize: 14, fontWeight: 700 }}>Nur ein halber Tag</span>
        <IosSchalter an={halberTag} label="Nur ein halber Tag" onChange={(an) => { setHalberTag(an); if (an) setBisTag(vonTag); }} />
      </div>

      <TextFeld label="Bemerkung" wert={grund} onChange={setGrund} placeholder="optional" kompakt />

      {TYP_INFO[typ].nachweis && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Arbeitsunfähigkeitsbescheinigung</span>
          {nachweis ? (
            <div className="flex items-center" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--fill)' }}>
              <FileArrowUp size={15} weight="fill" color="var(--acc)" className="shrink-0" />
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 600 }}>{nachweis.name}</span>
              <button data-tip="Datei entfernen" type="button" aria-label="Datei entfernen" onClick={() => setNachweis(null)} className="immo-tippziel flex items-center justify-center"
                style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' }}>
                <X size={12} weight="bold" />
              </button>
            </div>
          ) : (
            <label data-tip="Datei wählen – PDF oder Bild" className="immo-zeile-hover flex cursor-pointer items-center justify-center"
              style={{ gap: 8, padding: '14px 12px', borderRadius: 14, border: '1.5px dashed var(--line)', fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>
              <FileArrowUp size={16} weight="bold" /> Datei wählen (Bild oder PDF)
              <input type="file" accept="image/*,application/pdf" className="hidden"
                onChange={async (e) => {
                  const datei = e.target.files?.[0];
                  e.target.value = '';
                  if (!datei) return;
                  const hoch = await nachweisHochladen(mitarbeiterId, datei);
                  if (hoch.ok) setNachweis({ path: hoch.path, name: hoch.name });
                  else setFehler(hoch.fehler);
                }} />
            </label>
          )}
          <p className="m-0" style={{ fontSize: 12, color: 'var(--ink3)' }}>Die Bescheinigung sehen nur Sie selbst und die Personalabteilung.</p>
        </div>
      )}

      <div style={{ padding: '12px 14px', borderRadius: 14, background: 'var(--acc-soft)', fontSize: 13, color: 'var(--ink2)' }}>
        {TYP_INFO[typ].urlaubstage
          ? <>Das sind <b style={{ color: 'var(--acc)', fontWeight: 800 }}>{de(tage)} Urlaubstage</b>.</>
          : <>Wochenenden und Feiertage zählen nicht mit.</>}
        {feiertageImZeitraum.length > 0 && (
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ink3)' }}>Im Zeitraum: {feiertageImZeitraum.join(', ')}</div>
        )}
      </div>
    </DialogHuelle>
  );
}
